import React, { useEffect, useRef, useState } from 'react';

/**
 * SoundOrb: esfera 3D con ondas de sonido internas.
 * - Idle/Escuchando : pulso suave azul
 * - Hablando        : ondas reactivas al audio en tiempo real
 * - Procesando      : rotación y color ámbar
 */
export function SoundOrb({ audioTrack, agentState, size = 160 }) {
  const canvasRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef    = useRef(null);
  const ctxRef     = useRef(null);

  const colors = {
    connecting: { main: '#6b7280', glow: '#6b728044' },
    listening:  { main: '#3b82f6', glow: '#3b82f666' },
    thinking:   { main: '#f59e0b', glow: '#f59e0b66' },
    speaking:   { main: '#22c55e', glow: '#22c55e66' },
  };
  const col = colors[agentState] || colors.connecting;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = size;
    const H = canvas.height = size;
    const cx = W / 2, cy = H / 2, r = W * 0.38;

    // Conectar Web Audio si hay track
    if (audioTrack && agentState === 'speaking') {
      try {
        const ac  = new AudioContext();
        ctxRef.current = ac;
        const src = ac.createMediaStreamSource(new MediaStream([audioTrack]));
        const an  = ac.createAnalyser();
        an.fftSize = 128;
        src.connect(an);
        analyserRef.current = an;
      } catch (_) {}
    } else {
      analyserRef.current = null;
      const oldCtx = ctxRef.current;
      if (oldCtx && oldCtx.state !== 'closed') {
        oldCtx.close().catch(() => {});
      }
      ctxRef.current = null;
    }

    let tick = 0;

    const draw = () => {
      animRef.current = requestAnimationFrame(draw);
      tick += 0.03;
      ctx.clearRect(0, 0, W, H);

      // ── Ondas externas (rings) ──────────────────────────────────────────
      const ringCount = 3;
      for (let i = 0; i < ringCount; i++) {
        const phase  = (tick * 0.8 + i * 0.6) % (Math.PI * 2);
        const expand = agentState === 'speaking' ? 0.55 : 0.25;
        const rr     = r + (Math.sin(phase) * 0.5 + 0.5) * r * expand;
        const alpha  = 1 - (rr - r) / (r * expand + 1);

        ctx.beginPath();
        ctx.arc(cx, cy, rr, 0, Math.PI * 2);
        ctx.strokeStyle = col.main + Math.floor(alpha * 80).toString(16).padStart(2,'0');
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // ── Esfera base (gradiente 3D) ──────────────────────────────────────
      const grad = ctx.createRadialGradient(
        cx - r * 0.3, cy - r * 0.3, r * 0.05,
        cx, cy, r
      );
      grad.addColorStop(0,   col.main + 'ff');
      grad.addColorStop(0.4, col.main + 'cc');
      grad.addColorStop(1,   '#0a0a0a00');

      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      // ── Brillo superior (efecto 3D) ─────────────────────────────────────
      const shine = ctx.createRadialGradient(
        cx - r * 0.25, cy - r * 0.3, 0,
        cx - r * 0.1,  cy - r * 0.1, r * 0.55
      );
      shine.addColorStop(0, 'rgba(255,255,255,0.35)');
      shine.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = shine;
      ctx.fill();

      // ── Ondas internas de audio ─────────────────────────────────────────
      const an = analyserRef.current;
      if (an && agentState === 'speaking') {
        const data = new Uint8Array(an.frequencyBinCount);
        an.getByteFrequencyData(data);

        const wavePoints = 64;
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();

        ctx.beginPath();
        for (let i = 0; i <= wavePoints; i++) {
          const angle  = (i / wavePoints) * Math.PI * 2 - Math.PI / 2;
          const idx    = Math.floor((i / wavePoints) * data.length);
          const amp    = (data[idx] / 255) * r * 0.45;
          const radius = r * 0.5 + amp;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.18)';
        ctx.fill();
        ctx.restore();

      } else {
        // Onda idle suave
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.clip();

        const wavePoints = 64;
        ctx.beginPath();
        for (let i = 0; i <= wavePoints; i++) {
          const angle  = (i / wavePoints) * Math.PI * 2 - Math.PI / 2;
          const amp    = Math.sin(angle * 3 + tick * 2) * r * 0.08;
          const radius = r * 0.55 + amp;
          const x = cx + Math.cos(angle) * radius;
          const y = cy + Math.sin(angle) * radius;
          i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.closePath();
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        ctx.fill();
        ctx.restore();
      }

      // ── Glow exterior ───────────────────────────────────────────────────
      const pulse = Math.sin(tick * 2) * 0.5 + 0.5;
      const glow  = ctx.createRadialGradient(cx, cy, r * 0.8, cx, cy, r * 1.4);
      glow.addColorStop(0, col.main + Math.floor(pulse * 60).toString(16).padStart(2,'0'));
      glow.addColorStop(1, col.main + '00');
      ctx.beginPath();
      ctx.arc(cx, cy, r * 1.4, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();
    };

    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      const ac = ctxRef.current;
      if (ac && ac.state !== 'closed') {
        ac.close().catch(() => {});
      }
      ctxRef.current = null;
    };
  }, [audioTrack, agentState, size, col.main]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
}
